import { Dispatch, SetStateAction, useCallback, useEffect } from "react";
// components
import MultiselectBar from "../multiselect-bar";
import DownloadTaskStatus from "../download-task-status";
import UploadTaskStatus from "../upload-task-status";
// types
import type { SelectedItem, Task, WatchTask } from "../..";
import type { BatchEntryOpsFunc } from "contexts/orchid-drive/components/orchidDriveProvider";
// hooks
import useToggleModal from "hooks/useToggleModal";
import { useTreeWidgetContext } from "../tree-widget/contexts/TreeWidgetContext";
import { AmplitudePageName, track } from "utils/amplitude";

const ANALYTICS_COMPONENT_NAME = "bottom-bar";

export type UploadTaskStatusProps = {
  storageDrive: StorageDrive;
  watchTasks: WatchTask[];
  setWatchTasks: Dispatch<SetStateAction<WatchTask[]>>;
  onError: (arg: string | Error) => void;
};

type Props = {
  selected: SelectedItem[];
  selectedId: string;
  tasks: Task[];
  onDownload: () => void;
  onCancelSelected: () => void;
  onCancelDownload: (arg: Task) => void;
  onCancelAllDownloads: () => void;
  batchEntryOps: BatchEntryOpsFunc;
  analyticsPageName: AmplitudePageName;
} & UploadTaskStatusProps;

const BottomBar = ({
  selected,
  selectedId,
  tasks,
  onDownload,
  onCancelSelected,
  onCancelDownload,
  onCancelAllDownloads,
  batchEntryOps,
  storageDrive,
  analyticsPageName,
  ...uploadTaskStatusProps
}: Props) => {
  const { show: showTreeWidget, open: openTreeWidget } = useTreeWidgetContext();

  const {
    show: showStatus,
    open: openStatus,
    close: closeStatus,
  } = useToggleModal(false);
  const {
    show: showBar,
    open: openBar,
    close: closeBar,
  } = useToggleModal(false);

  // on tasks length change
  useEffect(() => {
    const nextShow =
      selected.length > 0 && tasks.every((el) => el.id !== selectedId);

    return nextShow ? openBar() : closeBar();
  }, [closeBar, openBar, selected.length, selectedId, showTreeWidget, tasks]);

  // close task status on no task
  useEffect(() => {
    // close if there is no more tasks in progress
    if (!tasks.length) {
      closeStatus();
    } else {
      openStatus();
    }
  }, [tasks.length, closeStatus, openStatus]);

  const handleMove = useCallback(
    () => openTreeWidget && openTreeWidget(selected),
    [openTreeWidget, selected],
  );

  return (
    <>
      {showBar && (
        <MultiselectBar
          selected={selected}
          onCancel={onCancelSelected}
          onDownload={onDownload}
          {...(storageDrive !== "note-attachment" && {
            onMove: handleMove,
          })}
          storageDrive={storageDrive}
          batchEntryOps={batchEntryOps}
          analyticsPageName={analyticsPageName}
          analyticsComponentName={ANALYTICS_COMPONENT_NAME}
        />
      )}

      <UploadTaskStatus
        storageDrive={storageDrive}
        {...uploadTaskStatusProps}
      />

      {showStatus && tasks.length && (
        <DownloadTaskStatus
          tasks={tasks}
          show={showStatus}
          onHide={() => {
            onCancelAllDownloads();
            closeStatus();
          }}
          onCancel={(e) => {
            track(
              analyticsPageName,
              ["downloads-widget", "cancel-download"],
              "click",
            );
            onCancelDownload(e);
          }}
        />
      )}
    </>
  );
};

export default BottomBar;
